import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Typography,
  Statistic,
  Row,
  Col,
  message,
  Select,
  Tag,
  Tooltip,
  Progress,
  Alert,
  Form,
  Input,
  Collapse,
  Modal,
  Switch,
  Popconfirm,
  Divider
} from 'antd';
import {
  CloudOutlined,
  ReloadOutlined,
  ScheduleOutlined,
  ThunderboltOutlined,
  SunOutlined,
  InfoCircleOutlined,
  EnvironmentOutlined,
  KeyOutlined,
  SaveOutlined,
  CaretRightOutlined,
  GlobalOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  LinkOutlined,
  ExperimentOutlined
} from '@ant-design/icons';
import { useApiCall } from '../hooks/useApiCall';
import { unifiedApi } from '../services/unifiedApi';
import { useResponsive } from '../hooks/useResponsive';
import { PageLayout } from './common';
import { useSimpleStore } from '../stores/simpleStore';
import { getApiUrl } from '../utils/api';

const { Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;
const { Panel } = Collapse;

interface WeatherData {
  id: number;
  route_id: number;
  route_name: string;
  route_number: string;
  location_name: string;
  temperature: number;
  condition: string;
  humidity: number;
  forecast_datetime: string;
  updated_at: string;
}

interface WeatherSummary {
  totalRoutes: number;
  routesWithWeather: number;
  lastUpdate: string;
  coveragePercent: number;
}

interface SchedulerStatus {
  isRunning: boolean;
  nextRun: string;
  lastRun: string;
  jobStatus: string;
}

interface NewsSettings {
  id: number;
  company_id: number;
  company_name: string;
  route_id?: number;
  route_number?: string;
  route_name?: string;
  source_name: string;
  rss_url: string;
  is_active: boolean;
  last_updated?: string;
  created_at: string;
  updated_at: string;
}

interface Company {
  id: number;
  name: string;
  code: string;
}

interface Route {
  id: number;
  route_number: string;
  route_name: string;
  status: string;
}

const WeatherManagement: React.FC = () => {
  const [form] = Form.useForm();
  const [newsForm] = Form.useForm();
  const [weatherData, setWeatherData] = useState<WeatherData[]>([]);
  const [weatherSummary, setWeatherSummary] = useState<WeatherSummary | null>(null);
  const [schedulerStatus, setSchedulerStatus] = useState<SchedulerStatus | null>(null);
  const [selectedRouteId, setSelectedRouteId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [routes, setRoutes] = useState([]);
  const [userRole, setUserRole] = useState<string>('');
  const [testingApiKey, setTestingApiKey] = useState(false);
  const [savingApiKey, setSavingApiKey] = useState(false);
  const [currentApiKey, setCurrentApiKey] = useState<string>('');
  const [showApiKeyCard, setShowApiKeyCard] = useState<boolean>(false);
  const [apiKeyCollapsed, setApiKeyCollapsed] = useState<boolean>(true); // Collapse 상태

  // 뉴스 관련 상태
  const [newsSettings, setNewsSettings] = useState<NewsSettings[]>([]);
  const [_companies, setCompanies] = useState<Company[]>([]);
  const [newsRoutes, setNewsRoutes] = useState<Route[]>([]);
  const [newsLoading, setNewsLoading] = useState(false);
  const [newsModalVisible, setNewsModalVisible] = useState(false);
  const [editingNewsId, setEditingNewsId] = useState<number | null>(null);
  const [testingRss, setTestingRss] = useState(false);
  const [rssTestResult, setRssTestResult] = useState<any>(null);
  const [newsCollapsed, setNewsCollapsed] = useState<boolean>(true);

  const { execute: callApi } = useApiCall();
  const { isMobile } = useResponsive();
  const { user, selectedCompanyId, selectedCompanyName } = useSimpleStore();

  useEffect(() => {
    // 사용자 역할 확인
    const token = localStorage.getItem('adminToken');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setUserRole(payload.role);
      } catch (error) {
        console.error('토큰 파싱 오류:', error);
      }
    }
    
    // API 키를 먼저 로드한 후 다른 데이터 로드
    const initializeData = async () => {
      await loadApiKey(); // API 키를 먼저 로드
      loadWeatherSummary();
      loadRoutes();
      loadCompanies();
      loadNewsSettings();
      if (userRole === 'admin') {
        loadSchedulerStatus();
      }
    };

    initializeData();
  }, [userRole]);

  // API 키 로드
  const loadApiKey = async () => {
    try {
      console.log('🔑 기상청 API 키 로드 시작...');
      
      const result = await callApi(() =>
        fetch(getApiUrl('/api/app-config'), {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
            'Content-Type': 'application/json'
          }
        }).then(res => res.json())
      );
      
      console.log('📥 API 설정 조회 결과:', result);
      
      if (result.success && result.data?.config?.WEATHER_API_KEY) {
        const apiKey = result.data.config.WEATHER_API_KEY;
        console.log('✅ 저장된 API 키 발견:', apiKey ? '***키 존재***' : '키 없음');
        
        setCurrentApiKey(apiKey);
        form.setFieldsValue({ WEATHER_API_KEY: apiKey });
        
        // API 키가 있어도 무조건 접힌 상태로 시작
        setShowApiKeyCard(true);  // 카드는 항상 보이도록
        setApiKeyCollapsed(true); // 내용은 무조건 접어둠
        console.log('📁 API 키 설정 카드 무조건 접기');
        
      } else {
        console.log('⚠️ 저장된 API 키가 없음 - 설정 카드도 접기');
        // API 키가 없어도 무조건 접힌 상태로 시작
        setShowApiKeyCard(true);
        setApiKeyCollapsed(true); // 무조건 접어둠
        setCurrentApiKey('');
      }
    } catch (error) {
      console.error('❌ API 키 로드 실패:', error);
      // 오류 시에도 무조건 접힌 상태로 시작
      setShowApiKeyCard(true);
      setApiKeyCollapsed(true);
      setCurrentApiKey('');
    }
  };

  // API 키 테스트 핸들러
  const handleTestApiKey = async () => {
    try {
      const currentValues = form.getFieldsValue();
      const apiKey = currentValues.WEATHER_API_KEY;
      
      if (!apiKey || apiKey.trim() === '') {
        message.warning('API 키를 입력한 후 테스트해주세요.');
        return;
      }
      
      setTestingApiKey(true);
      message.loading('API 키 유효성 검사 중...', 0.5);
      
      const result = await callApi(() => unifiedApi.weather.testApiKey(apiKey));
      
      if (result?.success) {
        message.success({
          content: `✅ API 키 유효성 검사 성공! 테스트 위치: ${result.data.tested_location}`,
          duration: 5
        });
      } else {
        message.error({
          content: `❌ ${result?.error || 'API 키가 유효하지 않습니다'}`,
          duration: 8
        });
      }
      
    } catch (error: any) {
      const errorMessage = error.message || 'API 키 테스트에 실패했습니다.';
      
      message.error({
        content: `❌ ${errorMessage}`,
        duration: 8
      });
    } finally {
      setTestingApiKey(false);
    }
  };

  // API 키 저장 핸들러
  const handleSaveApiKey = async () => {
    try {
      const currentValues = form.getFieldsValue();
      const apiKey = currentValues.WEATHER_API_KEY;
      
      if (!apiKey || apiKey.trim() === '') {
        message.warning('API 키를 입력해주세요.');
        return;
      }
      
      setSavingApiKey(true);
      
      const result = await callApi(() =>
        fetch(getApiUrl('/api/app-config'), {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            config: { WEATHER_API_KEY: apiKey }
          })
        }).then(res => res.json())
      );
      
      if (result.success) {
        message.success('기상청 API 키가 저장되었습니다');
        setCurrentApiKey(apiKey);
        setApiKeyCollapsed(true); // API 키 저장 후 카드 접기
        console.log('💾 API 키 저장 완료 - 카드 자동 접기');
      }
    } catch (error) {
      console.error('API 키 저장 실패:', error);
      message.error('API 키를 저장할 수 없습니다');
    } finally {
      setSavingApiKey(false);
    }
  };

  // 날씨 요약 정보 로드
  const loadWeatherSummary = async () => {
    try {
      const result = await callApi(() => unifiedApi.weather.summary());
      if (result?.success) {
        setWeatherSummary(result.data);
      }
    } catch (error) {
      console.error('날씨 요약 정보 로드 실패:', error);
    }
  };

  // 노선 목록 로드
  const loadRoutes = async () => {
    try {
      // 모든 운수사의 노선 목록을 가져오기 위해 company-routes API 사용
      const result = await callApi(() => unifiedApi.routes.list());
      if (result?.success) {
        setRoutes(result.data);
      } else {
        // 대안으로 직접 fetch 사용
        const response = await fetch('/api/company-routes', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
            'Content-Type': 'application/json'
          }
        });
        const data = await response.json();
        if (data.success) {
          setRoutes(data.data);
        }
      }
    } catch (error) {
      console.error('노선 목록 로드 실패:', error);
    }
  };

  // 스케줄러 상태 로드 (admin만)
  const loadSchedulerStatus = async () => {
    if (userRole !== 'admin') return;
    
    try {
      const result = await callApi(() => unifiedApi.weather.schedulerStatus());
      if (result?.success) {
        setSchedulerStatus(result.data);
      }
    } catch (error) {
      console.error('스케줄러 상태 로드 실패:', error);
    }
  };

  // 특정 노선 날씨 정보 로드
  const loadRouteWeather = async (routeId: number) => {
    setLoading(true);
    try {
      const result = await callApi(() => unifiedApi.weather.routeWeather(routeId, 24));
      if (result?.success) {
        setWeatherData(result.data);
      }
    } catch (error) {
      console.error('노선 날씨 정보 로드 실패:', error);
      message.error('노선 날씨 정보를 불러올 수 없습니다');
    } finally {
      setLoading(false);
    }
  };

  // 날씨 정보 수동 업데이트 (admin만)
  const handleUpdateWeather = async () => {
    if (userRole !== 'admin') return;
    
    setUpdating(true);
    try {
      const result = await callApi(() => unifiedApi.weather.update());
      if (result?.success) {
        message.success('날씨 정보 업데이트가 완료되었습니다');
        loadWeatherSummary();
        if (selectedRouteId) {
          loadRouteWeather(selectedRouteId);
        }
      }
    } catch (error) {
      console.error('날씨 정보 업데이트 실패:', error);
      message.error('날씨 정보 업데이트에 실패했습니다');
    } finally {
      setUpdating(false);
    }
  };

  // 노선 선택 핸들러
  const handleRouteSelect = (routeId: number) => {
    setSelectedRouteId(routeId);
    loadRouteWeather(routeId);
  };

  // 뉴스 관련 함수들
  // 운수사 목록 로드
  const loadCompanies = async () => {
    const result = await callApi(() => unifiedApi.companies.list());
    if (result?.success) {
      setCompanies(result.data || []);
    }
  };

  // 뉴스 설정 목록 로드
  const loadNewsSettings = async () => {
    setNewsLoading(true);
    try {
      const params = user?.role === 'admin' ? {} : { company_id: user?.company_id };
      const result = await callApi(() => unifiedApi.news.list(params));
      if (result?.success) {
        setNewsSettings(result.data || []);
      }
    } finally {
      setNewsLoading(false);
    }
  };

  // 선택된 운수사의 노선 목록 로드
  const loadNewsRoutes = async (companyId: number) => {
    const result = await callApi(() => unifiedApi.routes.listByCompany(companyId));
    if (result?.success) {
      setNewsRoutes(result.data || []);
    }
  };

  // RSS URL 테스트
  const testRssUrl = async () => {
    const rssUrl = newsForm.getFieldValue('rss_url');
    if (!rssUrl) {
      message.warning('먼저 RSS URL을 입력해주세요.');
      return;
    }

    setTestingRss(true);
    setRssTestResult(null);

    try {
      const result = await callApi(() => unifiedApi.news.testRss({ rss_url: rssUrl }));
      if (result?.success) {
        setRssTestResult(result.data);
        message.success('RSS 피드 테스트가 완료되었습니다.');
      }
    } finally {
      setTestingRss(false);
    }
  };

  // 뉴스 설정 저장
  const handleNewsSave = async () => {
    try {
      const values = await newsForm.validateFields();

      // 선택된 운수사 ID 설정
      if (!values.company_id) {
        if (selectedCompanyId) {
          values.company_id = selectedCompanyId;
        } else {
          message.error('운수사를 선택해주세요.');
          return;
        }
      }

      let result;
      if (editingNewsId) {
        result = await callApi(() => unifiedApi.news.update(editingNewsId, values));
        if (result?.success) {
          message.success('뉴스 설정이 수정되었습니다.');
        }
      } else {
        result = await callApi(() => unifiedApi.news.create(values));
        if (result?.success) {
          message.success('뉴스 설정이 생성되었습니다.');
        }
      }

      if (result?.success) {
        handleNewsCancel();
        await loadNewsSettings();
      }
    } catch (error) {
      console.error('Form validation error:', error);
    }
  };

  // 뉴스 설정 삭제
  const handleNewsDelete = async (id: number) => {
    const result = await callApi(() => unifiedApi.news.delete(id));
    if (result?.success) {
      message.success('뉴스 설정이 삭제되었습니다.');
      await loadNewsSettings();
    }
  };

  // 뉴스 모달 열기
  const showNewsModal = (record?: NewsSettings) => {
    if (!record && !selectedCompanyId) {
      message.warning('먼저 좌측 메뉴에서 운수사를 선택해주세요.');
      return;
    }

    setEditingNewsId(record?.id || null);
    setNewsModalVisible(true);
    setRssTestResult(null);

    if (record) {
      newsForm.setFieldsValue({
        company_id: record.company_id,
        route_id: record.route_id,
        source_name: record.source_name,
        rss_url: record.rss_url,
        is_active: record.is_active
      });
      if (record.company_id) {
        loadNewsRoutes(record.company_id);
      }
    } else {
      newsForm.resetFields();
      setNewsRoutes([]);
      newsForm.setFieldsValue({
        company_id: selectedCompanyId,
        is_active: true
      });
      if (selectedCompanyId) {
        loadNewsRoutes(selectedCompanyId);
      }
    }
  };

  // 뉴스 모달 닫기
  const handleNewsCancel = () => {
    setNewsModalVisible(false);
    setEditingNewsId(null);
    newsForm.resetFields();
    setNewsRoutes([]);
    setRssTestResult(null);
  };

  // 날씨 상태 아이콘
  const getWeatherIcon = (condition: string) => {
    if (condition?.includes('맑음') || condition?.includes('sunny')) {
      return <SunOutlined style={{ color: '#faad14' }} />;
    }
    return <CloudOutlined style={{ color: '#1890ff' }} />;
  };

  // 온도 색상
  const getTemperatureColor = (temp: number) => {
    if (temp >= 30) return '#ff4d4f';
    if (temp >= 20) return '#faad14';
    if (temp >= 10) return '#52c41a';
    return '#1890ff';
  };

  // 테이블 컬럼 정의
  const columns = [
    {
      title: '시간',
      dataIndex: 'forecast_datetime',
      key: 'forecast_datetime',
      render: (datetime: string) => {
        const date = new Date(datetime);
        return (
          <div>
            <div>{date.toLocaleDateString('ko-KR')}</div>
            <Text type="secondary">{date.toLocaleTimeString('ko-KR', { 
              hour: '2-digit', 
              minute: '2-digit' 
            })}</Text>
          </div>
        );
      }
    },
    {
      title: '위치',
      dataIndex: 'location_name',
      key: 'location_name',
      render: (location: string) => location || '정보 없음'
    },
    {
      title: '날씨',
      dataIndex: 'condition',
      key: 'condition',
      render: (condition: string) => (
        <Space>
          {getWeatherIcon(condition)}
          <span>{condition}</span>
        </Space>
      )
    },
    {
      title: '온도',
      dataIndex: 'temperature',
      key: 'temperature',
      render: (temp: number) => (
        <Text style={{ color: getTemperatureColor(temp), fontWeight: 'bold' }}>
          {temp}°C
        </Text>
      )
    },
    {
      title: '습도',
      dataIndex: 'humidity',
      key: 'humidity',
      render: (humidity: number) => `${humidity}%`
    },
    {
      title: '업데이트',
      dataIndex: 'updated_at',
      key: 'updated_at',
      render: (updated: string) => {
        const date = new Date(updated);
        return (
          <Tooltip title={date.toLocaleString('ko-KR')}>
            <Text type="secondary">{date.toLocaleTimeString('ko-KR')}</Text>
          </Tooltip>
        );
      }
    }
  ];

  // 뉴스 테이블 컬럼 정의
  const newsColumns = [
    {
      title: '운수사',
      dataIndex: 'company_name',
      key: 'company_name',
      width: 120,
      render: (text: string) => <Text strong>{text}</Text>
    },
    {
      title: '노선',
      key: 'route',
      width: 100,
      render: (record: NewsSettings) => (
        record.route_number ? (
          <Tag color="blue">{record.route_number}</Tag>
        ) : (
          <Tag color="default">전체</Tag>
        )
      )
    },
    {
      title: '소스명',
      dataIndex: 'source_name',
      key: 'source_name',
      width: 120
    },
    {
      title: 'RSS URL',
      dataIndex: 'rss_url',
      key: 'rss_url',
      width: 200,
      render: (url: string) => (
        <Tooltip title={url}>
          <a href={url} target="_blank" rel="noopener noreferrer">
            <LinkOutlined /> {url.length > 30 ? url.substring(0, 30) + '...' : url}
          </a>
        </Tooltip>
      )
    },
    {
      title: '상태',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 60,
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'success' : 'error'}>
          {isActive ? '활성' : '비활성'}
        </Tag>
      )
    },
    {
      title: '마지막 업데이트',
      dataIndex: 'last_updated',
      key: 'last_updated',
      width: 120,
      render: (date: string) => (
        date ? new Date(date).toLocaleString('ko-KR') : '-'
      )
    },
    {
      title: '작업',
      key: 'actions',
      width: 100,
      fixed: 'right' as const,
      render: (record: NewsSettings) => (
        <Space>
          <Tooltip title="수정">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => showNewsModal(record)}
            />
          </Tooltip>
          <Popconfirm
            title="삭제하시겠습니까?"
            description="이 작업은 되돌릴 수 없습니다."
            onConfirm={() => handleNewsDelete(record.id)}
            okText="삭제"
            cancelText="취소"
          >
            <Tooltip title="삭제">
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <PageLayout
      title="날씨 + 뉴스 관리"
      subtitle="기상청 API를 통한 날씨 정보 관리와 RSS 뉴스 소스 관리를 통합 제공합니다"
      actions={[
        {
          key: 'refresh',
          label: '새로고침',
          icon: <ReloadOutlined />,
          onClick: loadWeatherSummary,
          disabled: loading
        }
      ]}
    >
      {/* 액션 버튼 */}
      <div style={{ marginBottom: 24 }}>
        <Space>
          {userRole === 'admin' && (
            <Button
              type="primary"
              icon={<ThunderboltOutlined />}
              onClick={handleUpdateWeather}
              loading={updating}
            >
              날씨 업데이트
            </Button>
          )}
        </Space>
      </div>

      {/* 날씨 요약 통계 - 크기 통일 */}
      {weatherSummary && (
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={12} sm={12} md={6} lg={6}>
            <Card style={{ minHeight: 120 }}>
              <Statistic
                title="총 노선 수"
                value={weatherSummary.totalRoutes}
                prefix={<EnvironmentOutlined />}
                valueStyle={{ fontSize: '24px' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={12} md={6} lg={6}>
            <Card style={{ minHeight: 120 }}>
              <Statistic
                title="날씨 정보 보유 노선"
                value={weatherSummary.routesWithWeather}
                prefix={<CloudOutlined />}
                valueStyle={{ fontSize: '24px' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={12} md={6} lg={6}>
            <Card style={{ minHeight: 120 }}>
              <Statistic
                title="날씨 정보 커버리지"
                value={weatherSummary.coveragePercent}
                suffix="%"
                prefix={<Progress type="circle" size="small" percent={weatherSummary.coveragePercent} showInfo={false} />}
                valueStyle={{ fontSize: '24px' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={12} md={6} lg={6}>
            <Card style={{ minHeight: 120 }}>
              <Statistic
                title="마지막 업데이트"
                value={weatherSummary.lastUpdate ? new Date(weatherSummary.lastUpdate).toLocaleTimeString('ko-KR', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '정보 없음'}
                prefix={<ScheduleOutlined />}
                valueStyle={{ fontSize: '16px' }}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* 기상청 API 키 설정 (admin만, Collapse 형태) */}
      {userRole === 'admin' && showApiKeyCard && (
        <Collapse 
          style={{ marginBottom: 24 }}
          activeKey={apiKeyCollapsed ? [] : ['api-key-panel']}
          onChange={(keys) => {
            const isOpen = keys.includes('api-key-panel');
            setApiKeyCollapsed(!isOpen);
            console.log(isOpen ? '📂 API 키 설정 펼치기' : '📁 API 키 설정 접기');
          }}
          expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
          ghost
        >
          <Panel
            key="api-key-panel"
            header={
              <Space>
                <KeyOutlined />
                <span style={{ fontWeight: 500 }}>기상청 API 키 설정</span>
                {currentApiKey && (
                  <Tag color="green">설정됨</Tag>
                )}
              </Space>
            }
          >
            <Alert 
              message="기상청 API 키 설정" 
              description="공공데이터포털(data.go.kr)에서 발급받은 기상청 단기예보조회서비스 API 키를 입력하세요. API 키가 변경되면 자동으로 적용됩니다." 
              type="info" 
              showIcon
              style={{ marginBottom: 16 }}
            />
            
            <Form 
              form={form}
              layout="vertical"
              initialValues={{ WEATHER_API_KEY: currentApiKey }}
              key={`form_${currentApiKey}`} // API 키가 변경될 때 Form 재렌더링
            >
              <Row gutter={16}>
                <Col span={24}>
                  <Form.Item
                    name="WEATHER_API_KEY"
                    label="기상청 API 서비스 키"
                    rules={[
                      { required: true, message: '기상청 API 키를 입력해주세요.' }
                    ]}
                  >
                    <TextArea 
                      placeholder="공공데이터포털에서 발급받은 기상청 단기예보조회서비스 API 키를 입력하세요"
                      rows={3}
                      showCount
                    />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Space>
                    <Button 
                      type="dashed" 
                      onClick={handleTestApiKey}
                      loading={testingApiKey}
                    >
                      🔍 API 키 유효성 검사
                    </Button>
                    <Button 
                      type="primary"
                      icon={<SaveOutlined />}
                      onClick={handleSaveApiKey}
                      loading={savingApiKey}
                    >
                      API 키 저장
                    </Button>
                  </Space>
                </Col>
              </Row>
            </Form>
          </Panel>
        </Collapse>
      )}

      {/* 스케줄러 상태 (admin만) */}
      {userRole === 'admin' && schedulerStatus && (
        <Alert
          message="자동 날씨 업데이트 스케줄러 - 1시간마다 자동 실행"
          description={
            <Space direction="vertical" size={4}>
              <div>상태: <Tag color={schedulerStatus.isRunning ? 'green' : 'red'}>
                {schedulerStatus.isRunning ? '실행 중 (1시간 간격)' : '중지됨'}
              </Tag></div>
              {schedulerStatus.nextRun && <div>다음 실행: {new Date(schedulerStatus.nextRun).toLocaleString('ko-KR')}</div>}
              {schedulerStatus.lastRun && <div>마지막 실행: {new Date(schedulerStatus.lastRun).toLocaleString('ko-KR')}</div>}
              <div style={{ fontSize: '12px', color: '#666' }}>스케줄러가 매시간 정각에 모든 노선의 날씨 정보를 자동으로 업데이트합니다.</div>
            </Space>
          }
          type={schedulerStatus.isRunning ? 'success' : 'warning'}
          icon={<InfoCircleOutlined />}
          style={{ marginBottom: 24 }}
        />
      )}

      {/* 노선 선택 */}
      <Card title="노선별 날씨 조회" style={{ marginBottom: 24 }}>
        <Space wrap>
          <Text>노선 선택:</Text>
          <Select
            placeholder="날씨를 확인할 노선을 선택하세요"
            style={{ minWidth: 250 }}
            onChange={handleRouteSelect}
            value={selectedRouteId}
            showSearch
            optionFilterProp="children"
            loading={routes.length === 0}
          >
            {routes.map((route: any) => (
              <Option key={route.id} value={route.id}>
                {route.route_number} - {route.route_name}
              </Option>
            ))}
          </Select>
        </Space>
      </Card>

      {/* 날씨 정보 테이블 */}
      <Card 
        title={
          selectedRouteId 
            ? `${(routes as any[]).find((r: any) => r.id === selectedRouteId)?.route_number || ''} 노선 날씨 예보`
            : '노선 날씨 정보'
        }
        extra={
          weatherData.length > 0 && (
            <Text type="secondary">
              {weatherData.length}개 데이터
            </Text>
          )
        }
      >
        <Table
          columns={columns}
          dataSource={weatherData}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: isMobile ? 5 : 10,
            showTotal: (total, range) => `${range[0]}-${range[1]} / ${total}개`,
            showSizeChanger: !isMobile
          }}
          scroll={{ x: isMobile ? 800 : undefined }}
          locale={{
            emptyText: selectedRouteId 
              ? '해당 노선의 날씨 정보가 없습니다'
              : '노선을 선택하면 날씨 정보를 확인할 수 있습니다'
          }}
        />
      </Card>

      {/* 뉴스 RSS 관리 섹션 */}
      <Collapse
        style={{ marginTop: 24 }}
        activeKey={newsCollapsed ? [] : ['news-panel']}
        onChange={(keys) => {
          const isOpen = keys.includes('news-panel');
          setNewsCollapsed(!isOpen);
          console.log(isOpen ? '📂 뉴스 RSS 관리 펼치기' : '📁 뉴스 RSS 관리 접기');
        }}
        expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
        ghost
      >
        <Panel
          key="news-panel"
          header={
            <Space>
              <GlobalOutlined />
              <span style={{ fontWeight: 500 }}>뉴스 RSS 관리</span>
              <Tag color="blue">{newsSettings.length}개 설정</Tag>
            </Space>
          }
        >
          <Alert
            message="뉴스 RSS 관리 안내"
            description="RSS URL을 추가하면 Android 단말기에서 자동으로 뉴스를 가져와 표시합니다. 노선을 지정하지 않으면 해당 운수사의 모든 노선에 적용됩니다."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: '14px', color: '#666' }}>
              🚢 선택된 운수사: <strong>{selectedCompanyName || '선택되지 않음'}</strong>
            </div>
            <Space>
              <Button
                icon={<ReloadOutlined />}
                onClick={loadNewsSettings}
                loading={newsLoading}
              >
                새로고침
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => showNewsModal()}
              >
                뉴스 소스 추가
              </Button>
            </Space>
          </div>

          <Table
            columns={newsColumns}
            dataSource={newsSettings}
            rowKey="id"
            loading={newsLoading}
            size="small"
            scroll={{ x: 900 }}
            pagination={{
              total: newsSettings.length,
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `총 ${total}개 항목`
            }}
          />
        </Panel>
      </Collapse>

      {/* 뉴스 설정 모달 */}
      <Modal
        title={editingNewsId ? '뉴스 설정 수정' : '뉴스 설정 추가'}
        open={newsModalVisible}
        onOk={handleNewsSave}
        onCancel={handleNewsCancel}
        width={800}
        confirmLoading={newsLoading}
        destroyOnClose
        okText={editingNewsId ? '수정' : '추가'}
        cancelText="취소"
      >
        <Form
          form={newsForm}
          layout="vertical"
          initialValues={{
            is_active: true
          }}
        >
          <Form.Item
            name="company_id"
            label="운수사"
            rules={[{ required: true, message: '운수사를 선택해주세요.' }]}
            hidden
          >
            <Input />
          </Form.Item>

          {/* 운수사 정보 표시 */}
          <div style={{ marginBottom: 16, padding: 12, backgroundColor: '#f0f2f5', borderRadius: 6 }}>
            <Text strong>선택된 운수사: </Text>
            <Text>{selectedCompanyName || '선택되지 않음'}</Text>
          </div>

          <Form.Item
            name="route_id"
            label={
              <span>
                노선
                <Tooltip title="선택하지 않으면 해당 운수사의 모든 노선에 적용됩니다.">
                  <InfoCircleOutlined style={{ marginLeft: 4, color: '#1890ff' }} />
                </Tooltip>
              </span>
            }
          >
            <Select
              placeholder={newsRoutes.length > 0 ? "노선 선택 (선택사항)" : "노선을 불러오는 중..."}
              allowClear
              showSearch
              optionFilterProp="children"
              disabled={newsRoutes.length === 0}
              notFoundContent={newsRoutes.length === 0 ? "노선 정보를 불러오는 중입니다." : "노선이 없습니다."}
            >
              {newsRoutes.map(route => (
                <Option key={route.id} value={route.id}>
                  {route.route_number} - {route.route_name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="source_name"
            label="소스명"
            rules={[{ required: true, message: '소스명을 입력해주세요.' }]}
          >
            <Input placeholder="예: 한국경제, 연합뉴스" />
          </Form.Item>

          <Form.Item
            name="rss_url"
            label={
              <span>
                RSS URL
                <Button
                  type="link"
                  icon={<ExperimentOutlined />}
                  size="small"
                  onClick={testRssUrl}
                  loading={testingRss}
                  style={{ marginLeft: 8 }}
                >
                  테스트
                </Button>
              </span>
            }
            rules={[
              { required: true, message: 'RSS URL을 입력해주세요.' },
              { type: 'url', message: '유효한 URL 형식이 아닙니다.' }
            ]}
          >
            <Input placeholder="https://example.com/rss" />
          </Form.Item>

          <Form.Item
            name="is_active"
            label="활성 상태"
            valuePropName="checked"
          >
            <Switch checkedChildren="활성" unCheckedChildren="비활성" />
          </Form.Item>

          {rssTestResult && (
            <>
              <Divider />
              <Alert
                message="RSS 피드 테스트 결과"
                type="success"
                showIcon
                style={{ marginBottom: 16 }}
              />

              <div style={{ marginBottom: 16 }}>
                <Text strong>피드 정보:</Text>
                <div style={{ marginLeft: 16, marginTop: 8 }}>
                  <div><Text type="secondary">제목:</Text> {rssTestResult.feed_title}</div>
                  <div><Text type="secondary">총 항목:</Text> {rssTestResult.total_items}개</div>
                  <div><Text type="secondary">테스트 URL:</Text> {rssTestResult.tested_url}</div>
                </div>
              </div>

              {rssTestResult.sample_items?.length > 0 && (
                <div>
                  <Text strong>샘플 뉴스 (최신 3개):</Text>
                  <div style={{ marginLeft: 16, marginTop: 8 }}>
                    {rssTestResult.sample_items.map((item: any, index: number) => (
                      <div key={index} style={{ marginBottom: 8, padding: 8, backgroundColor: '#f5f5f5', borderRadius: 4 }}>
                        <div><Text strong>{item.title}</Text></div>
                        <div><Text type="secondary">{item.description?.substring(0, 100)}...</Text></div>
                        <div><Text type="secondary" style={{ fontSize: '12px' }}>{item.pubDate}</Text></div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </Form>
      </Modal>
    </PageLayout>
  );
};

export default WeatherManagement;